import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Web3 from 'web3';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Updated imports
import withdraw_grey from '../styles/assets/withdraw_grey.svg';
import india_flag from '../styles/assets/india_flag.png';
import btx_locked from '../styles/assets/btx_locked.svg';
import btx_unlocked from '../styles/assets/btx_unlocked.svg';

type Numbers = number;

const StyledCard = muiStyled(Card)(({ theme }) => ({
  backgroundColor: '#26283E',
  color: theme.palette.common.white,
}));

interface CoinItem {
  name: string;
  amount: Numbers;
  value: string;
  network: string;
  isReward?: boolean;
  icon: string;
}

const StyledAvatar = muiStyled(Avatar)(({ theme }) => ({
  backgroundColor: 'transparent',
  width: 40,
  height: 40,
  '& img': {
    width: '80%',
    height: '80%',
    objectFit: 'contain',
  },
}));

const TopContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #000000;
  color: #ffffff;
  padding: 20px;
`;

const BalanceDisplay = styled(Typography)`
  font-size: 48px;
  margin-bottom: 20px;
`;

const WalletAddressContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
`;

const AddressText = styled(Typography)`
  color: #4a90e2;
  margin-right: 10px;
`;

const WithdrawButton = styled.div`
  background-color: #26283E;
  border-radius: 12px;
  padding: 12px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 200px;
`;

const WithdrawIconContainer = styled.div`
  background-color: #000;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WithdrawText = styled(Typography)`
  color: #ffffff;
  font-size: 16px;
`;

const btxTokenABI = [
  {
    "constant": true,
    "inputs": [{ "name": "_owner", "type": "address" }],
    "name": "balanceOf",
    "outputs": [{ "name": "balance", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [{ "name": "_owner", "type": "address" }],
    "name": "transferrableBalanceOf",
    "outputs": [{ "name": "balance", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [{ "name": "_owner", "type": "address" }],
    "name": "rewardBalanceOf",
    "outputs": [{ "name": "balance", "type": "uint256" }],
    "type": "function"
  }
];

const RoninWalletDashboard = () => {
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [btxBalance, setBtxBalance] = useState<Numbers>(0);
  const [btxRewardBalance, setBtxRewardBalance] = useState<Numbers>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    // const storedAddress = localStorage.getItem('walletAddress');
    const walletId = JSON.parse(localStorage.getItem('gw-wallet-info') || '{}');
    const storedAddress = walletId.address;
    if (storedAddress) {
      setWalletAddress(storedAddress);
      fetchBTXBalances(storedAddress);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchBTXBalances = async (address: string) => {
    try {
      const web3 = new Web3('https://saigon-testnet.roninchain.com/rpc');
      const btxTokenAddress = '0xfc3313da2290f8c00d526a52a66867f8b559c520';
      const btxContract = new web3.eth.Contract(btxTokenABI as any, btxTokenAddress);
  
      const [totalBalance, transferrableBalance, rewardBalance] = await Promise.all([
        btxContract.methods.balanceOf(address).call(),
        btxContract.methods.transferrableBalanceOf(address).call(),
        btxContract.methods.rewardBalanceOf(address).call()
      ]);
  
      const safeFromWei = (balance: unknown): number => {
        if (typeof balance === 'string' || typeof balance === 'number' || typeof balance === 'bigint') {
          return Number(web3.utils.fromWei(balance.toString(), 'ether'));
        } else if (Array.isArray(balance) && balance.length > 0) {
          return safeFromWei(balance[0]);
        } else {
          console.error('Unexpected balance type:', balance);
          return 0;
        }
      };
  
      setBtxBalance(safeFromWei(transferrableBalance));
      setBtxRewardBalance(safeFromWei(rewardBalance));
  
      // console.log('Processed balances:', {
      //   btxBalance: safeFromWei(transferrableBalance),
      //   btxRewardBalance: safeFromWei(rewardBalance)
      // });
  
    } catch (error) {
      console.error('Error fetching BTX balances:', error);
      setBtxBalance(0);
      setBtxRewardBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const coinItems: CoinItem[] = [
    {
      name: 'BTX',
      amount: btxBalance,
      value: `$${(btxBalance * 0.001).toFixed(2)}`,
      network: 'Ronin',
      icon: btx_unlocked
    },
    {
      name: 'BTX Reward',
      amount: btxRewardBalance,
      value: `$${(btxRewardBalance * 0.001).toFixed(2)}`,
      network: 'Ronin',
      isReward: true,
      icon: btx_locked
    },
  ];

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletAddress).then(() => {
      alert('Address copied to clipboard!');
    });
  };

  const handleNavigation = () => {
    if (!btxBalance || btxBalance === 0) {
      toast('You have no unlocked BTX');
      return;
    }
    const searchParams = new URLSearchParams();
    searchParams.set("address", walletAddress);
    searchParams.set("gc_balance", btxBalance.toString());
    navigate(`/wallet-host/razorpay-offramp/?${searchParams.toString()}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalValue = (btxBalance + btxRewardBalance) * 0.001;

  return (
    <Box sx={{ minHeight: '100vh', color: 'text.primary' }}>
      <TopContainer>
        <BalanceDisplay>${totalValue.toFixed(2)}</BalanceDisplay>
        <WalletAddressContainer>
          <AddressText>
            {walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-8)}` : 'No wallet connected'}
          </AddressText>
          <Button onClick={handleCopyAddress}>
            <ContentCopyIcon fontSize="small" style={{ color: 'white' }} />
          </Button>
        </WalletAddressContainer>
        <WithdrawButton onClick={handleNavigation}>
          <WithdrawIconContainer>
            <img src={withdraw_grey} alt="Withdraw" style={{ width: '20px', height: '20px' }} />
          </WithdrawIconContainer>
          <div style={{display:"flex", justifyContent:"cennter",
            alignItems:"center"
          }}>
            <WithdrawText>Withdraw BTX</WithdrawText>
            <img src={india_flag} alt="India flag" style={{ width: '20px', height: '20px' }} />
          </div>
        </WithdrawButton>
      </TopContainer>

      <StyledCard sx={{ mx: 2 }}>
        <CardContent>
          <List>
            {coinItems.map((item, index) => (
              <ListItem key={index}>
                <ListItemAvatar>
                <StyledAvatar>
                  <img src={item.icon} alt={item.name} />
                </StyledAvatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${item.amount} ${item.name}`}
                  secondary={item.network}
                />
                <Typography>{item.value}</Typography>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default RoninWalletDashboard;